import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";
import CarDetailsModal from "../components/CarDetailsModal";
import tickImage from "../assets/images/tickm.png";
const CarDetails = () => {
  const navigate = useNavigate();
  const { carmodelId: carmodelIdParam } = useParams();
  const { language, t } = useLanguage();

  // ============================================================
  // STATE
  // ============================================================

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMore, setShowMore] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [pickupLocation, setPickupLocation] = useState("");
  const [returnLocation, setReturnLocation] = useState("");

  const [pickupDate, setPickupDate] = useState("");
  const [returnDate, setReturnDate] = useState("");

  const [clientid, setClientid] = useState("");
  const [clientemail, setClientemail] = useState("");
  
  const [pickupTime, setPickupTime] = useState("10:00");
  const [returnTime, setReturnTime] = useState("10:00");

  const [bookingError, setBookingError] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showRegularClientLogin, setShowRegularClientLogin] = useState(false);  
  // ============================================================
  // IMPORTANT CONTENT
  // ============================================================

  const [importantContents, setImportantContents] = useState({
    page9: {
      cont_title: "",
      cont_details: "",
    },
    page10: {
      cont_title: "",
      cont_details: "",
    },
    page11: {
      cont_title: "",
      cont_details: "",
    },
  });

  const [loadingImportantContents, setLoadingImportantContents] =
    useState(true);

  // ============================================================
  // TRANSLATION
  // ============================================================

  const tt = (key, fallback = "") => {
    try {
      return t(`cardetails.${key}`, fallback);
    } catch (err) {
      return fallback;
    }
  };

  // ============================================================
  // LANGUAGE ID
  // ============================================================

  const getLanguageId = () => {
    if (!language) {
      return 1;
    }

    if (typeof language === "object") {
      return (
        Number(
          language.lang_id ||
            language.language_id ||
            language.id
        ) || 1
      );
    }

    return Number(language) || 1;
  };

  const languageId = getLanguageId();

  // ============================================================
  // TODAY
  // ============================================================

  const getToday = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const today = getToday();

  const handlePickupChange = (e) => {
    const selectedDate = e.target.value;
  
    setPickupDate(selectedDate);
  
    if (selectedDate) {
      const date = new Date(selectedDate);
      date.setDate(date.getDate() + 3);
  
      const formattedDate = date.toISOString().split("T")[0];
      setReturnDate(formattedDate);
    } else {
      setReturnDate("");
    }
  };
  // ============================================================
  // RESOLVE CAR MODEL ID
  // ============================================================

  const resolveCarmodelId = () => {
    if (carmodelIdParam) {
      const id = Number(carmodelIdParam);

      if (!Number.isNaN(id) && id > 0) {
        return id;
      }
    }

    try {
      const bookingCriteria =
        sessionStorage.getItem("bookingCriteria");

      if (bookingCriteria) {
        const parsed = JSON.parse(bookingCriteria);

        const id =
          parsed?.selectedCarId ||
          parsed?.carmodel_id ||
          parsed?.car_id;

        if (id) {
          const numericId = Number(id);

          if (!Number.isNaN(numericId) && numericId > 0) {
            return numericId;
          }
        }
      }
    } catch (err) {
      console.error("Unable to read bookingCriteria:", err);
    }

    try {
      const selectedCar =
        sessionStorage.getItem("selectedCar");

      if (selectedCar) {
        const parsed = JSON.parse(selectedCar);

        const id =
          parsed?.carmodel_id ||
          parsed?.car_id ||
          parsed?.id;

        if (id) {
          const numericId = Number(id);

          if (!Number.isNaN(numericId) && numericId > 0) {
            return numericId;
          }
        }
      }
    } catch (err) {
      console.error("Unable to read selectedCar:", err);
    }

    return null;
  };

  // ============================================================
  // IMAGE
  // ============================================================

  const getCarImage = (image) => {
    if (!image) {
      return "/images/car_34.jpg";
    }

    let imagePath = String(image).trim();

    if (!imagePath) {
      return "/images/car_34.jpg";
    }

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    const imageBase =
      IMAGE_BASE_URL || "http://localhost:5555";

    return `${imageBase}/${imagePath}`;
  };

  // ============================================================
  // CLEAN DATABASE HTML
  // ============================================================

  const cleanContentHtml = (html) => {
    if (!html) {
      return "";
    }

    return String(html)
      .replace(/<li>\s*<\/li>/gi, "")
      .replace(
        /<li>(?:\s|&nbsp;|<br\s*\/?>)*<\/li>/gi,
        ""
      )
      .replace(
        /<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi,
        ""
      )
      .replace(
        /<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi,
        ""
      );
  };

  // ============================================================
  // FETCH CONTENT PAGE
  // ============================================================

  const fetchContentPage = async (pageId, langId) => {
    try {
      const response = await api.get("/contents/content", {
        params: {
          page_id: pageId,
          lang_id: langId,
        },
      });

      const result = response.data;

      if (result && result.success && result.data) {
        if (Array.isArray(result.data)) {
          return result.data[0] || null;
        }

        return result.data;
      }

      return null;
    } catch (err) {
      console.error(
        `ERROR FETCHING CONTENT PAGE ${pageId}:`,
        err
      );

      return null;
    }
  };

  // ============================================================
  // FETCH IMPORTANT CONTENT
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const loadImportantContents = async () => {
      setLoadingImportantContents(true);

      const [page9, page10, page11] =
        await Promise.all([
          fetchContentPage(9, languageId),
          fetchContentPage(10, languageId),
          fetchContentPage(11, languageId),
        ]);

      if (cancelled) {
        return;
      }

      setImportantContents({
        page9: {
          cont_title: page9?.cont_title || "",
          cont_details: page9?.cont_details || "",
        },

        page10: {
          cont_title: page10?.cont_title || "",
          cont_details: page10?.cont_details || "",
        },

        page11: {
          cont_title: page11?.cont_title || "",
          cont_details: page11?.cont_details || "",
        },
      });

      setLoadingImportantContents(false);
    };

    loadImportantContents();

    return () => {
      cancelled = true;
    };
  }, [languageId]);

  // ============================================================
  // FETCH CAR DETAILS
  // ============================================================

  useEffect(() => {
    fetchCarDetails();
  }, [languageId, carmodelIdParam]);

  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      setError("");
      setShowMore(false);

      const carmodelId = resolveCarmodelId();

      if (!carmodelId) {
        setCar(null);

        setError(
          tt(
            "noCarSelected",
            "No car selected."
          )
        );

        setLoading(false);
        return;
      }

      console.log("FETCHING CAR DETAILS");
      console.log("Car Model ID:", carmodelId);
      console.log("Language ID:", languageId);

      const response = await api.get(
        `cars/${carmodelId}/details`,
        {
          params: {
            langid: languageId,
          },
        }
      );

      console.log("CAR DETAILS RESPONSE:", response.data);

      if (response.data?.success) {
        const carData = response.data.data || null;

        console.log("CAR DATA:", carData);
        console.log(
          "INCLUDED ITEMS:",
          carData?.includedItems
        );

        setCar(carData);
      } else {
        setCar(null);

        setError(
          tt(
            "carNotFound",
            "Car not found."
          )
        );
      }
    } catch (err) {
      console.error("CAR DETAILS API ERROR:", err);
      console.error("STATUS:", err.response?.status);
      console.error("RESPONSE:", err.response?.data);

      setCar(null);

      setError(
        tt(
          "loadError",
          "Unable to load car details."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FETCH LOCATIONS
  // ============================================================

  useEffect(() => {
    fetchLocations();
  }, [languageId]);

  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);

      const response = await api.get(
        "/locations/getLocations",
        {
          params: {
            city_id: 1,
            lang_id: languageId,
          },
        }
      );

      console.log(
        "LOCATIONS RESPONSE:",
        response.data
      );

      let locationData = [];

      if (Array.isArray(response.data)) {
        locationData = response.data;
      } else if (
        Array.isArray(response.data?.data)
      ) {
        locationData = response.data.data;
      } else if (
        Array.isArray(response.data?.locations)
      ) {
        locationData = response.data.locations;
      }

      setLocations(locationData);
    } catch (err) {
      console.error("LOCATION API ERROR:", err);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  // ============================================================
  // LOCATION HELPERS
  // ============================================================

  const getLocationId = (location) => {
    if (!location) {
      return "";
    }

    return (
      location.location_id ||
      location.locationid ||
      location.station_id ||
      location.stationid ||
      location.loc_id ||
      location.id ||
      ""
    );
  };

  const getLocationName = (location) => {
    if (!location) {
      return "";
    }

    return (
      location.location_name ||
      location.locationname ||
      location.station_name ||
      location.stationname ||
      location.loc_name ||
      location.name ||
      location.title ||
      ""
    );
  };

  // ============================================================
  // YES / NO
  // ============================================================

  const yesNo = (value) => {
    if (
      value === "Yes" ||
      value === "yes" ||
      value === 1 ||
      value === "1" ||
      value === true
    ) {
      return t("carparams.carparams7", "Yes")
    }

    return t("carparams.carparams8", "No")
  };

  // ============================================================
  // FEATURES
  // ============================================================

  const getFeatureValue = (item) => {
    if (
      item === undefined ||
      item === null
    ) {
      return "";
    }

    if (
      typeof item === "string" ||
      typeof item === "number"
    ) {
      return String(item).trim();
    }

    if (typeof item === "object") {
      return String(
        item.item_name ||
          item.name ||
          item.mldynsec_val ||
          item.value ||
          item.title ||
          item.label ||
          item.description ||
          ""
      ).trim();
    }

    return "";
  };

  const getAllFeatures = (carData) => {
    let source = [];

    if (
      Array.isArray(carData?.includedItems) &&
      carData.includedItems.length > 0
    ) {
      source = carData.includedItems;
    } else if (
      Array.isArray(carData?.features) &&
      carData.features.length > 0
    ) {
      source = carData.features;
    } else if (
      Array.isArray(carData?.included_items) &&
      carData.included_items.length > 0
    ) {
      source = carData.included_items;
    } else if (
      Array.isArray(carData?.carFeatures) &&
      carData.carFeatures.length > 0
    ) {
      source = carData.carFeatures;
    } else if (
      Array.isArray(carData?.car_features) &&
      carData.car_features.length > 0
    ) {
      source = carData.car_features;
    }

    return source
      .map(getFeatureValue)
      .filter(Boolean);
  };

  // ============================================================
  // SELECTED CAR ID
  // ============================================================

  const getSelectedCarId = () => {
    if (!car) {
      return resolveCarmodelId();
    }

    return (
      car.carmodel_id ||
      car.carmodelId ||
      car.car_model_id ||
      car.car_id ||
      car.id ||
      resolveCarmodelId()
    );
  };

  // ============================================================
  // CATEGORY
  // ============================================================

  const getCarCategoryId = () => {
    if (!car) {
      return "";
    }

    return (
      car.cat_id ||
      car.category_id ||
      car.catid ||
      car.categoryId ||
      ""
    );
  };

  // ============================================================
  // DRIVER AGE
  // ============================================================

  const getDriverAge = () => {
    if (!car) {
      return "";
    }

    return (
      car.driverAge ||
      car.driver_age ||
      ""
    );
  };

  // ============================================================
  // CONTINUE
  // ============================================================

  const handleContinue = async (event) => {
    event.preventDefault();

    setBookingError("");

    if (!pickupLocation) {
        alert("Please select pickup location.");
        return;
    }

    if (!returnLocation) {
      alert("Please select return location.");
      return;
    }

    if (!pickupDate) {
      alert("Please select pickup date.");
      return;
    }

    if (!returnDate) {
      alert("Please select return date.");
      return;
    }

    if (!pickupTime) {
      alert("Please select pickup time.");
      return;
    }

    if (!returnTime) {
      alert("Please select return time.");
      return;
    }

    const pickupDateObject =
      new Date(`${pickupDate}T00:00:00`);

    const returnDateObject =
      new Date(`${returnDate}T00:00:00`);

    if (returnDateObject < pickupDateObject) {
      setBookingError(
        tt(
          "invalidDates",
          "Return date cannot be before pickup date."
        )
      );
      return;
    }

    if (
      pickupDate === returnDate &&
      returnTime <= pickupTime
    ) {
      setBookingError(
        tt(
          "invalidTime",
          "Return time must be after pickup time."
        )
      );
      return;
    }

    const selectedCarId = getSelectedCarId();

    if (!selectedCarId) {
      setBookingError(
        tt(
          "noCarSelected",
          "No car selected."
        )
      );
      return;
    }

    const pickupTimeParts = pickupTime.split(":");
    const returnTimeParts = returnTime.split(":");

    const pickupHour =
      pickupTimeParts[0] || "10";

    const pickupMinute =
      pickupTimeParts[1] || "00";

    const returnHour =
      returnTimeParts[0] || "10";

    const returnMinute =
      returnTimeParts[1] || "00";

    const criteria = {
      cmbPickLocation: String(pickupLocation),
      pickdate: pickupDate,
      cmbpickHour: pickupHour,
      cmbpickmin: pickupMinute,

      cmbDropLocation: String(returnLocation),
      returndate: returnDate,
      cmbreturnHour: returnHour,
      cmbreturnmin: returnMinute,

      cmbcartype: getCarCategoryId(),
      cmbdrvage: getDriverAge(),

      clientid: "",
      clientemail: "",
      selectedlanguage: languageId,

      Editourcar: "",

      selectedCarId: Number(selectedCarId),
    };

    console.log(
      "BOOKING CRITERIA:",
      criteria
    );

    try {
      setBookingLoading(true);

      sessionStorage.setItem(
        "bookingCriteria",
        JSON.stringify(criteria)
      );

      if (car) {
        sessionStorage.setItem(
          "selectedCar",
          JSON.stringify(car)
        );
      }

      sessionStorage.setItem(
        "selectedCarId",
        String(selectedCarId)
      );

      navigate("/bookacarthree");
    } catch (err) {
      console.error(
        "BOOKING NAVIGATION ERROR:",
        err
      );

      setBookingError(
        tt(
          "bookingError",
          "Unable to continue booking. Please try again."
        )
      );
    } finally {
      setBookingLoading(false);
    }
  };

  // ============================================================
  // PREVIOUS
  // ============================================================

  const handleGoPrevious = () => {
    navigate(-1);
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">
            {tt("loading", "Loading...")}
          </span>
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error || !car) {
    return (
      <div className="p20">
        <div className="container container2">
          <div
            className="col-md-12 main-content"
            style={{ float: "left" }}
          >
            <div className="text-center py-5">
              <h4>
                {error ||
                  tt(
                    "carNotFound",
                    "Car not found."
                  )}
              </h4>

              <button
                type="button"
                className="btn btn-secondary1 py-3 px-4 mt-3"
                onClick={handleGoPrevious}
              >
                «{" "}
                {t(
                            "bookingengine.bookingengine12",
                            "Go Previous"
                          )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // NORMALIZE FEATURES
  // ============================================================

  const normalizedFeatures =
    Array.isArray(car.includedItems)
      ? car.includedItems
          .map((item) => {
            if (typeof item === "string") {
              return item;
            }

            if (
              item &&
              typeof item === "object"
            ) {
              return (
                item.item_name ||
                item.itemName ||
                item.name ||
                item.included_item_name ||
                item.includedItemName ||
                item.title ||
                ""
              );
            }

            return "";
          })
          .filter(Boolean)
      : [];

  const firstFeatures =
    normalizedFeatures.slice(0, 4);

  const extraFeatures =
    normalizedFeatures.slice(4);

  // ============================================================
  // CAR BASIC DATA
  // ============================================================

  const carTitle =
    car.name ||
    car.model ||
    [car.brand, car.model]
      .filter(Boolean)
      .join(" ") ||
    tt("car", "Car");

  const carSubtitle =
    car.category ||
    car.category_name ||
    car.cat_name ||
    "";

  const price =
    car.pricePerDay ??
    car.price_per_day ??
    car.price ??
    null;

  const image = getCarImage(car.image);

  // ============================================================
  // IMPORTANT CONTENT
  // ============================================================

  const page9Title =
    importantContents.page9.cont_title ||
    "Also Important to know";

  const page9Details =
    cleanContentHtml(
      importantContents.page9.cont_details
    );

  const page10Title =
    importantContents.page10.cont_title ||
    "Attention!!";

  const page10Details =
    cleanContentHtml(
      importantContents.page10.cont_details
    );

  const page11Title =
    importantContents.page11.cont_title ||
    "Enjoy your holidays";

  const page11Details =
    cleanContentHtml(
      importantContents.page11.cont_details
    );

  // ============================================================
  // OPEN MODAL
  // ============================================================

  const openDetailsModal = () => {
    setShowDetailsModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    document.body.style.overflow = "";
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <>
      {/* ======================================================
          BOOKING ENGINE
      ====================================================== */}

      <div
        className="booking-engine-wrapper"
        style={{
          width: "100%",
          display: "block",
          position: "relative",
          zIndex: 20,
        }}
      >
        <div
          className="ban hero-wrap ftco-degree-bg"
         
        >
          <div className="container">
            <div
              className=" ban
                row
                no-gutters
                slider-text
                justify-content-center
                align-items-center
              "
             
            >
              <div className="col-lg-8 pd34">
                <div className="text w-100 text-center">
                  <div className="col-md-12 d-flex align-items-center">
                    <form
                      className="request-form bg-primary"
                      onSubmit={handleContinue}
                      style={{
                        width: "100%",
                        display: "block",
                      }}
                    >
                      {/* PICKUP */}

                      <div
                        className="col-md-6"
                        style={{
                          float: "left",
                          padding: 0,
                        }}
                      >
                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                            textAlign: "left",
                          }}
                        >
                          <label className="label">
                          {t("bookingengine.bookingengine0", "Pick-up Location")}
                          </label>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <div
                            className="col-lg-12 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <div className="selectParent">
                              <select
                                className="arrow form-control select"
                                value={pickupLocation}
                                onChange={(e) => {
                                  setPickupLocation(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                disabled={
                                  locationsLoading ||
                                  bookingLoading
                                }
                              >
                                <option value="">
                                  {locationsLoading
                                    ? tt(
                                        "loading",
                                        "Loading..."
                                      )
                                    : t("bookingengine.bookingengine8", "Select location")}
                                </option>

                                {locations.map(
                                  (
                                    location,
                                    index
                                  ) => {
                                    const id =
                                      getLocationId(
                                        location
                                      );

                                    const name =
                                      getLocationName(
                                        location
                                      );

                                    return (
                                      <option
                                        key={
                                          id ||
                                          `pickup-${index}`
                                        }
                                        value={id || ""}
                                      >
                                        {name}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <label className="label" style={{marginRight: "52px",float: "left"}}>
                            {t("bookingengine.bookingengine1", "Pick-up Date & Time")}
                          </label>
                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="date"
                              className=" form-control"
                              value={pickupDate}
                              min={today}
                              disabled={bookingLoading}
                              onChange={handlePickupChange}
                            />
                          </div>

                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="time"
                              className="form-control"
                              value={pickupTime}
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setPickupTime(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* RETURN */}

                      <div
                        className="col-md-6"
                        style={{
                          float: "left",
                          padding: 0,
                        }}
                      >
                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                            textAlign: "left",
                          }}
                        >
                          <label className="label">
                             {t("bookingengine.bookingengine3", "Drop-off Location")}
                          </label>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          

                          <div
                            className="col-lg-12 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            
                            <div className="selectParent">
                              <select
                                className="arrow form-control select"
                                value={returnLocation}
                                onChange={(e) => {
                                  setReturnLocation(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                disabled={
                                  locationsLoading ||
                                  bookingLoading
                                }
                              >
                                <option value="">
                                  {locationsLoading
                                    ? tt(
                                        "loading",
                                        "Loading..."
                                      )
                                    : t("bookingengine.bookingengine8", "Select location")}
                                </option>

                                {locations.map(
                                  (
                                    location,
                                    index
                                  ) => {
                                    const id =
                                      getLocationId(
                                        location
                                      );

                                    const name =
                                      getLocationName(
                                        location
                                      );

                                    return (
                                      <option
                                        key={
                                          id ||
                                          `return-${index}`
                                        }
                                        value={id || ""}
                                      >
                                        {name}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                           <label className="label" style={{marginRight: "52px",float: "left"}}>
                            {t("bookingengine.bookingengine4", "Drop-off Date & Time")}
                          </label>
                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="date"
                              className=" form-control"
                              value={returnDate}
                              min={
                                pickupDate || today
                              }
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setReturnDate(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>

                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="time"
                              className="form-control"
                              value={returnTime}
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setReturnTime(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="clearfix"></div>

                      {/* ERROR */}

                      {bookingError && (
                        <div
                          className="col-md-12"
                          style={{
                            padding: "5px 0",
                            color: "#fff",
                            textAlign: "center",
                            fontWeight: 500,
                          }}
                        >
                          {bookingError}
                        </div>
                      )}

                      {/* BUTTONS */}

                      <div
                        className="col-md-12"
                        style={{
                          padding: 0,
                          float: "left",
                        }}
                      >
                        <div
                          className="form-group col-md-6"
                          style={{
                            float: "left",
                            padding:
                              "4px 0",
                          }}
                        >
                          <button
                            type="submit"
                            className="btn btn-secondary1"
                            disabled={
                              bookingLoading ||
                              locationsLoading
                            }
                            style={{
                              width: "100%",
                            }}
                          >
                            {bookingLoading
                              ? tt(
                                  "loading",
                                  "Loading..."
                                )
                              : t("bookingengine.bookingengine9", "Continue")}
                          </button>
                        </div>

                        <div
                          className="col-md-4"
                          style={{
                            float: "right",
                            padding:
                              "0 5px 0 0",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-secondary2 py-3 px-4"
                            onClick={() => setShowRegularClientLogin((prev) => !prev)}
                          >
                            {t("bookingengine.bookingengine24", "Regular client Login")}
                          </button>
                        </div>
                        {showRegularClientLogin && (
                        <div className="booking-client-row">

                          {/* CLIENT ID */}

                          <div className="booking-client-half">

                            <div className="form-group">

                              <input
                                type="text"
                                name="clientid"
                                className="frmtxtbox1"
                                maxLength="30"
                                value={clientid}
                                onChange={(e) => {
                                  setClientid(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                placeholder={`${t("bookingengine.bookingengine16", "Client Id.")} :`}
                              />

                              <label>
                                <span className="required-star">
                                  *
                                </span>
                                {t("othersnewstatics.othersnewstatic49", "When am I entitled to be a regular client")}?
                                     
                              </label>

                            </div>

                          </div>

                          {/* EMAIL */}

                          <div className="booking-client-half">

                            <div className="form-group">

                              <input
                                type="text"
                                name="clientemail"
                                className="frmtxtbox1"
                                maxLength="100"
                                value={
                                  clientemail
                                }
                                onChange={(e) => {
                                  setClientemail(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                placeholder={`${t("bookingengine.bookingengine17", "Email Id.")} :`}
                              />

                              <label>
                                <span className="required-star">
                                  *
                                </span>
                                {t("othersnewstatics.othersnewstatic48", "I forgot my regular client ID")}?
                              </label>

                            </div>

                          </div>

                        </div>
                      )} 
                      </div>

                      <div className="clearfix"></div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="clearfix"></div>

      {/* ======================================================
          CAR DETAILS
      ====================================================== */}

      <div className="p20">
        <div className="p0 container container2">
          <div
            className="col-md-12 main-content"
            style={{ float: "left" }}
          >
            <div className="rental-card">
              <div className="card-main-content">

                {/* TITLE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-9 col-lg-9"
                  style={{ float: "left" }}
                >
                  <h2 className="car-title">
                    {carTitle}
                  </h2>

                  <div className="car-subtitle">
                    {carSubtitle}
                  </div>
                </div>

                {/* PRICE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-3 col-lg-3"
                  style={{ float: "left" }}
                >
                  <div className="price-container">
                    <div className="price-label">
                    ({t("carparams.carparams0", "Per day")})
                    </div>

                    <div className="main-price">
                      {price !== null
                        ? `${Number(price).toFixed(
                            2
                          )} €`
                        : tt(
                            "contactForPrice",
                            "Contact for price"
                          )}
                    </div>
                  </div>
                </div>

                <div className="clearfix"></div>

                {/* IMAGE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="image-container">
                    <img
                      src={image}
                      alt={carTitle}
                      className="car-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "/images/car_34.jpg";
                      }}
                    />
                  </div>
                </div>

                {/* FEATURES */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="features-container">

                    <div className="specs-row">
                      <span className="spec-tag">
                        👤 {car.passengers || 0}
                      </span>

                      <span className="spec-tag">
                        🧳 {car.luggage || 0}
                      </span>

                      <span className="spec-tag">
                        ❄️{" "}
                        {yesNo(
                          car.airConditioning
                        )}
                      </span>

                      <span className="spec-tag">
                        🚪{" "}
                        {car.doors || "3"}
                      </span>
                    </div>

                    {firstFeatures.map(
                      (feature, index) => (
                        <div
                          className="feature-item"
                          key={`first-${index}`}
                        >
                          <span className="feature-icon">
                           <img
                             src={tickImage}
                             alt="Included"
                             className="feature-icon"
                             style={{
                               width: "20px",
                               height: "20px",
                               minWidth: "20px",
                               objectFit: "contain",
                               marginRight: "8px",
                               display: "inline-block",
                               verticalAlign: "middle",
                             }}
                           />
                          </span>

                          <strong>
                            {feature}
                          </strong>
                        </div>
                      )
                    )}

                    {extraFeatures.length > 0 && (
                      <>
                        <div
                          className="modify"
                          style={{
                            display: "block",
                            width: "100%",
                            marginTop: "10px",
                          }}
                        >
                          <button
                            type="button"
                            className="showmore"
                            onClick={() =>
                              setShowMore(
                                (prev) => !prev
                              )
                            }
                          >
                            {showMore
                              ? t(
                                "othersnewstatics.othersnewstatic51",
                                "Show less"
                              )
                              : (() => {
                                const [first, ...rest] = t(
                                  "othersnewstatics.othersnewstatic46",
                                  "Show More"
                                ).split(" ");
                                return `${first} ${extraFeatures.length} ${rest.join(" ")}`;
                              })()}
                          </button>
                        </div>

                        {showMore && (
                          <div
                            className="test1"
                            style={{
                              display: "block",
                              width: "100%",
                              height: "auto",
                              maxHeight: "none",
                              overflow: "visible",
                              visibility: "visible",
                              opacity: 1,
                            }}
                          >
                            <div
                              className="test-box1"
                              style={{
                                display: "block",
                                width: "100%",
                                height: "auto",
                                maxHeight: "none",
                                overflow: "visible",
                              }}
                            >
                              {extraFeatures.map(
                                (
                                  feature,
                                  index
                                ) => (
                                  <div
                                    className="feature-item"
                                    key={`extra-${index}`}
                                  >
                                    <span className="feature-icon">
                                     <img
                                       src={tickImage}
                                       alt="Included"
                                       className="feature-icon"
                                       style={{
                                         width: "20px",
                                         height: "20px",
                                         minWidth: "20px",
                                         objectFit: "contain",
                                         marginRight: "8px",
                                         display: "inline-block",
                                         verticalAlign: "middle",
                                       }}
                                     />
                                    </span>

                                    <strong>
                                      {feature}
                                    </strong>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ACTIONS */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="actions-container">
                    <div className="car-d">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={openDetailsModal}
                      >
                         {t(
                            "carparams.carparams2",
                            "Car details"
                          )}

                      </button>
                    </div>

                    <button
                      type="button"
                      className="book-btn"
                      onClick={handleGoPrevious}
                    >
                      «{" "}
                       {t(
                            "bookingengine.bookingengine12",
                            "Go Previous"
                          )}
                      
                    </button>
                  </div>
                </div>

                <div className="clearfix"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          IMPORTANT INFORMATION
      ====================================================== */}

      <div className="clearfix"></div>

      <div className="ft-bg">
        <div className="why-choose-us-area">
          <div className="container">
            <div className="row">

              <div
                className="col-md-12"
                align="center"
              >
                <div className="heading-section heading-section-white">
                  {loadingImportantContents ? (
                    <h2
                      className="aboutp"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      {tt(
                        "loading",
                        "Loading..."
                      )}
                    </h2>
                  ) : (
                    <h2
                      className="aboutp"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      {page9Title}
                    </h2>
                  )}
                </div>
              </div>

              {/* PAGE 9 */}

              <div
                className="col-md-6"
                style={{ padding: 0 }}
              >
                <div className="services services-2 w-100">
                  <div className="pr28 text-1 text w-100">
                    <div className="price-card">
                      <div className="content">
                        <h2>{page9Title}</h2>
                      </div>

                      {!loadingImportantContents &&
                        page9Details && (
                          <div
                            className="why-content-page9"
                            dangerouslySetInnerHTML={{
                              __html:
                                page9Details,
                            }}
                          />
                        )}

                      {!loadingImportantContents &&
                        !page9Details && (
                          <p>
                            {tt(
                              "noInformation",
                              "No information available."
                            )}
                          </p>
                        )}

                      <div className="clearfix"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PAGE 10 + 11 */}

              <div
                className="col-md-6"
                style={{ padding: 0 }}
              >
                <div className="pr28 services services-2 w-100">
                  <div className="price-card1">

                    <div className="content">
                      <h2>{page10Title}</h2>
                    </div>

                    {!loadingImportantContents &&
                      page10Details && (
                        <div
                          className="why-content-page10"
                          dangerouslySetInnerHTML={{
                            __html:
                              page10Details,
                          }}
                        />
                      )}

                    {!loadingImportantContents &&
                      !page10Details && (
                        <p>
                          {tt(
                            "noInformation",
                            "No information available."
                          )}
                        </p>
                      )}

                    <div
                      style={{
                        height: "20px",
                      }}
                    />

                    <div className="content">
                      <h2>{page11Title}</h2>
                    </div>

                    {!loadingImportantContents &&
                      page11Details && (
                        <div
                          className="why-content-page11"
                          dangerouslySetInnerHTML={{
                            __html:
                              page11Details,
                          }}
                        />
                      )}

                    {!loadingImportantContents &&
                      !page11Details && (
                        <p>
                          {tt(
                            "noInformation",
                            "No information available."
                          )}
                        </p>
                      )}

                    <div className="clearfix"></div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          CAR DETAILS MODAL (from components)
      ====================================================== */}

      <CarDetailsModal
        show={showDetailsModal}
        onClose={closeDetailsModal}
        car={car}
        loading={false}
        error=""
        tt={tt}
        getCarImage={getCarImage}
        getFeatures={getAllFeatures}
        onBook={() => {
          closeDetailsModal();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </>
  );
};

export default CarDetails;
